
import React from 'react'
import { StatusIndicator } from '@/components/brand/StatusIndicator'
import { smartleadAPI } from '@/lib/smartlead-api'
import { isSupabaseConfigured } from '@/lib/supabase'

interface SystemHealthIndicatorProps {
  lastUpdated: Date | null
  error: string | null
  usingCache: boolean
}

export function SystemHealthIndicator({ 
  lastUpdated, 
  error, 
  usingCache 
}: SystemHealthIndicatorProps) {
  const getSystemStatus = () => {
    if (error && !usingCache) return 'critical'
    if (error || usingCache || !smartleadAPI.isConfigured()) return 'warning'
    return 'healthy'
  }

  const getStatusLabel = () => {
    // Check if API key is from environment (production setup)
    const hasEnvKey = !!import.meta.env.VITE_SMARTLEAD_API_KEY
    
    if (!smartleadAPI.isConfigured()) {
      // Only show "Demo mode" if no env key exists
      return hasEnvKey ? 'Initializing...' : 'Demo mode'
    }
    if (error && !usingCache) return 'API unavailable'
    if (usingCache) return 'Using cached data'
    if (!isSupabaseConfigured()) return 'Limited features'
    return 'All systems operational'
  }

  const formatLastUpdated = () => {
    if (!lastUpdated) return 'Never'
    const now = new Date()
    const diff = now.getTime() - lastUpdated.getTime()
    const minutes = Math.floor(diff / 60000)
    
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }

  return (
    <div className="flex items-center gap-3 px-4 py-2 bg-white rounded-lg border border-gray-200">
      <StatusIndicator 
        status={getSystemStatus()}
        label={getStatusLabel()}
      />
      <div className="text-xs text-gray-500">
        Last updated: {formatLastUpdated()}
      </div>
    </div>
  )
}
